
import { memo } from 'react';
import { ArrowRight, Settings, Braces } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WorkflowCard } from '../WorkflowCard';
import { aslAccentColors } from '../WorkflowCard.types';
import { StateBadges } from '../StateBadges';
import { BreakpointMarker } from '../BreakpointMarker';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';

interface PassNodeProps {
    id: string;
    data: StateNodeData;
    selected?: boolean;
}

/**
 * Pass Node - Represents an ASL Pass state.
 * 
 * Pass states are "lightweight" compared to Task - they pass data through
 * with optional transformation. Shows data transform badges for Result,
 * ResultPath, Parameters, etc.
 */
export const PassNode = memo(function PassNode({ id, data, selected }: PassNodeProps) {
    const { openSettings } = useNodeSettings();
    const { 
        label, 
        state, 
        isStart, 
        isEnd, 
        layoutDirection,
        isDebugMode,
        isCurrentNode,
        hasBreakpoint,
        onToggleBreakpoint,
    } = data;

    // Check if this is a transform (has Result or Parameters)
    const hasTransform = state.Result !== undefined || 
        (state.Parameters && Object.keys(state.Parameters).length > 0);

    return (
        <WorkflowCard
            selected={selected}
            accentColor={aslAccentColors.pass}
            isStart={isStart}
            isEnd={isEnd}
            handleColor="!bg-slate-400 !border-slate-500"
            layoutDirection={layoutDirection}
            isCurrentNode={isCurrentNode}
        >
            {/* Breakpoint Marker (debug mode only) */}
            {isDebugMode && onToggleBreakpoint && (
                <BreakpointMarker
                    nodeId={id}
                    isEnabled={hasBreakpoint ?? false}
                    isDebugMode={isDebugMode}
                    isCurrent={isCurrentNode ?? false}
                    onClick={onToggleBreakpoint}
                />
            )}

            <WorkflowCard.Icon>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-500">
                    {hasTransform ? <Braces size={18} /> : <ArrowRight size={20} />}
                </div>
            </WorkflowCard.Icon>

            <WorkflowCard.Name>
                <span>{label}</span>
                <span className="ml-1.5 text-[10px] font-normal text-slate-400">· Pass</span>
            </WorkflowCard.Name>

            {isStart && <WorkflowCard.Badge>Start</WorkflowCard.Badge>}

            <WorkflowCard.Description>
                {state.Comment ? (
                    <p className="leading-tight">{state.Comment}</p>
                ) : state.Result !== undefined ? (
                    <p className="leading-tight">
                        Result:{' '}
                        {typeof state.Result === 'object'
                            ? JSON.stringify(state.Result).slice(0, 25) + '...'
                            : String(state.Result)}
                    </p>
                ) : (
                    <p className="leading-tight text-slate-400">Pass-through</p>
                )}
            </WorkflowCard.Description>

            {/* Feature Badges */}
            <WorkflowCard.BottomLeft>
                <StateBadges state={state} />
            </WorkflowCard.BottomLeft>

            <WorkflowCard.BottomRight>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={14} />
                </Button>
            </WorkflowCard.BottomRight>
        </WorkflowCard>
    );
});
